import React from 'react';
import styled from 'styled-components';
import { Box, Heading } from '@99designs/emu';
import { spaces } from '@99designs/emu/settings';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { BodyText, TileSelect } from '@99designs/spa-common';
import { Answer } from '../../components/Step/Answer';
import { ReactComponent as ContestSVG } from './contest-illustration.svg';
import { ReactComponent as ProjectSVG } from './project-illustration.svg';
import type { StepComponentProps, StepHeadingProps } from '../../components/Onboarder/Config';
import type { Data } from '../config';

export type WorkingMethodValue = 'project' | 'contest';

export function WorkingMethodStepInputs({ data, onChange }: StepComponentProps<Data>) {
    return (
        <TileSelect<WorkingMethodValue>
            name="workingMethod"
            value={data.workingMethod}
            options={[
                {
                    label: (
                        <Details
                            icon={<ProjectSVG />}
                            heading={__('Work directly')}
                            body={__(
                                "We'll help you find a designer with just the right skill set, and you'll collaborate one-on-one with them on your project."
                            )}
                        />
                    ),
                    value: 'project',
                },
                {
                    label: (
                        <Details
                            icon={<ContestSVG />}
                            heading={__('Start a contest')}
                            body={__(
                                'Invite our entire creative community to participate in your project. Designers submit ideas and you pick your favorite.'
                            )}
                        />
                    ),
                    value: 'contest',
                },
            ]}
            onChange={(value, name) => {
                const newData: Data = {
                    ...data,
                    [name]: value,
                };
                onChange(newData);
            }}
        />
    );
}

const InlineSVG = styled.div`
    width: 280px;
    margin-bottom: ${spaces['2bu']};

    svg {
        display: inline-block;
        width: inherit;
        height: inherit;
    }
`;

const StyledHeading = styled(Heading)`
    padding-bottom: ${spaces['0.25bu']};
`;

export interface DetailsProps {
    icon: React.ReactNode;
    heading: string;
    body: string;
}

function Details({ icon, heading, body }: DetailsProps) {
    return (
        <Box padding="3bu" display="flex" flexDirection="column" alignItems="center">
            <InlineSVG>{icon}</InlineSVG>
            <StyledHeading appearanceLevel={5} accessibilityLevel={4}>
                {heading}
            </StyledHeading>
            <BodyText>{body}</BodyText>
        </Box>
    );
}

export const answerCopy = (): { [key: string]: string } => ({
    project: __('work directly'),
    contest: __('start a contest'),
});

export const questionCopy = () => __('I would prefer to {answer}');

export function WorkingMethodStepHeading({ data }: StepHeadingProps<Data>) {
    const answer = data.workingMethod ? answerCopy()[data.workingMethod] : '';

    return (
        <FormattedMessage message={questionCopy()} values={{ answer: <Answer text={answer} /> }} />
    );
}
